import { useCallback, useState, type ReactNode } from "react";
import { bekreftKode, sendKode } from "../state/innlogging";

/**
 * Innlogging på nettflata. Engangskode på e-post, ingen passord.
 *
 * Begrunnelsen for metoden er juridisk og står i `docs/09-juridisk.md` §
 * «Særlig om innloggingen». Kort: ingenting å lekke, og ingen tredjepart som
 * får vite når crewet jobber.
 *
 * ========  KODE, IKKE LENKE  ========
 *
 * Malen er felles med feltappen, og den sender kode fordi en lenke på iOS
 * alltid åpner i standardnettleseren — se `supabase/templates/magic_link.html`.
 * Her gir det en fordel til: ingen token havner i adressefeltet, og da heller
 * ikke i historikken på en maskin som deles i et kontrollrom.
 *
 * ========  BARE INNLOGGINGEN  ========
 *
 * «Logget inn, men ikke medlem noe sted» sto her før, som et eget kort med
 * teksten «Prodleder legger deg inn». Det var en blindvei for den som skulle
 * rigge den første produksjonen selv. Den tilstanden er nå konsollen — se
 * `screens/KonsollScreen.tsx` — og dette skjermbildet gjør én ting.
 *
 * ========  SAMME SVAR PÅ UKJENT ADRESSE  ========
 *
 * Skriver man en adresse som ikke finnes, kommer kodefeltet likevel. Det er
 * med vilje: svarte flata «den adressen finnes ikke», ville den fortalt en
 * fremmed hvem som jobber på produksjonen.
 */
export function InnloggingScreen(): JSX.Element {
  const [epost, setEpost] = useState("");
  const [kode, setKode] = useState("");
  const [fase, setFase] = useState<"epost" | "sender" | "kode" | "bekrefter">("epost");
  const [feil, setFeil] = useState<string | null>(null);

  const send = useCallback(async (): Promise<void> => {
    if (epost.trim().length === 0 || fase === "sender") return;
    setFase("sender");
    setFeil(null);
    const svar = await sendKode(epost);
    if (svar.ok) { setFase("kode"); return; }
    setFase("epost");
    setFeil(svar.feil ?? "Koden kunne ikke sendes.");
  }, [epost, fase]);

  /* Lykkes den, finnes det en økt — og porten i App.tsx bytter flate av seg
     selv gjennom `paaEndring`. Derfor er det ingen «inne»-tilstand her. */
  const bekreft = useCallback(async (): Promise<void> => {
    if (kode.trim().length === 0 || fase === "bekrefter") return;
    setFase("bekrefter");
    setFeil(null);
    const svar = await bekreftKode(epost, kode);
    if (svar.ok) return;
    setFase("kode");
    setFeil("Koden stemmer ikke, eller den har gått ut. Be om en ny.");
  }, [epost, kode, fase]);

  if (fase === "kode" || fase === "bekrefter") {
    return (
      <Kort>
        <span className="innlogging__tittel">Skriv koden</span>
        <p className="innlogging__tekst">
          Finnes {epost.trim()} i en produksjon, ligger det en kode i innboksen
          nå.
        </p>

        <form
          className="innlogging__skjema"
          onSubmit={(e) => { e.preventDefault(); void bekreft(); }}
        >
          <input
            className="innlogging__felt innlogging__felt--kode mono"
            type="text"
            inputMode="numeric"
            /* Nettleseren og passordappen kjenner igjen engangskoder på
               dette ordet, og fyller feltet fra varselet. */
            autoComplete="one-time-code"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
            value={kode}
            onChange={(e) => { setKode(e.target.value); }}
            placeholder="00000000"
            aria-label="Engangskode"
          />
          <button
            type="submit"
            className="synk__knapp synk__knapp--hoved"
            disabled={fase === "bekrefter" || kode.trim() === ""}
          >
            {fase === "bekrefter" ? "Sjekker …" : "Logg inn"}
          </button>
        </form>

        {feil === null ? null : <p className="innlogging__feil">{feil}</p>}

        <button
          type="button"
          className="innlogging__tilbake"
          onClick={() => { setKode(""); setFeil(null); setFase("epost"); }}
        >
          Skrev du feil adresse? Begynn på nytt
        </button>

        <span className="innlogging__merknad">
          Koden virker én gang og går ut etter en time.
        </span>
      </Kort>
    );
  }

  return (
    <Kort>
      <span className="innlogging__tittel">Logg inn</span>
      <p className="innlogging__tekst">
        Skriv e-postadressen din, så sender vi en kode. Ingen passord.
      </p>

      <form
        className="innlogging__skjema"
        onSubmit={(e) => { e.preventDefault(); void send(); }}
      >
        <input
          className="innlogging__felt"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          autoFocus
          value={epost}
          onChange={(e) => { setEpost(e.target.value); }}
          placeholder="deg@produksjonen.no"
          aria-label="E-postadresse"
        />
        <button
          type="submit"
          className="synk__knapp synk__knapp--hoved"
          disabled={fase === "sender" || epost.trim() === ""}
        >
          {fase === "sender" ? "Sender …" : "Send kode"}
        </button>
      </form>

      {feil === null ? null : <p className="innlogging__feil">{feil}</p>}

      <span className="innlogging__merknad">
        Kontoen opprettes når du blir lagt inn i en produksjon, ikke her. Det er
        bare e-postadressen vi ber om.
      </span>
    </Kort>
  );
}

/**
 * Kortet alle tilstandene bor i.
 *
 * Merket staar her og ikke i en toppstripe: dette er den eneste flata uten
 * krom, og den som moeter en halvferdig innlogging skal se HVA hen logger inn
 * i. Inne i flata bor merket i menyen.
 */
function Kort({ children }: { readonly children: ReactNode }): JSX.Element {
  return (
    <div className="innlogging">
      <div className="innlogging__kort">
        <span className="merke" aria-hidden="true" />
        {children}
      </div>
    </div>
  );
}
